import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * ICD-10 coding suggestions for the patient file (tasks/icd10-suggestion-pipeline.md §11).
 *
 * Every route is patient-scoped, which is what the server authorises against — the suggestion id
 * alone proves nothing, so it is always sent alongside the patient id.
 */
@Injectable({ providedIn: 'root' })
export class IcdSuggestionService {
  private apiUrl = 'api';

  constructor(private http: HttpClient) { }

  private authHeaders() {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  /** Pending suggestions only. Accepted/rejected rows are history and live in the audit trail. */
  getForPatient(patientId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/patients/${patientId}/icd-suggestions`, this.authHeaders());
  }

  acceptSuggestion(patientId: string, suggestionId: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/patients/${patientId}/icd-suggestions/${suggestionId}/accept`,
      {},
      this.authHeaders()
    );
  }

  /**
   * `reason` is optional. The response carries `data.suppressed` — false means the code may be
   * proposed again, so the UI must not claim the dismissal was permanent.
   */
  rejectSuggestion(patientId: string, suggestionId: string, reason?: string | null): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/patients/${patientId}/icd-suggestions/${suggestionId}/reject`,
      { reason: reason || null },
      this.authHeaders()
    );
  }

  /** Coding history. Pass `code` for the per-code popover on an AI-applied chip. */
  getAudit(patientId: string, code?: string): Observable<any> {
    const query = code ? `?code=${encodeURIComponent(code)}` : '';
    return this.http.get(`${this.apiUrl}/patients/${patientId}/icd-audit${query}`, this.authHeaders());
  }

  /**
   * The codes this patient no longer carries.
   *
   * Codes are never hard-deleted — removal flips `active` — so this is history that has always
   * been in the database with nothing on the platform able to read it. Read-only; there is no
   * restore route.
   */
  getArchivedCodes(patientId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/patients/${patientId}/icd-codes/archived`, this.authHeaders());
  }

  /**
   * The patient's standing clinical context in point form (§11.14).
   *
   * `summary` is null when the patient has not been analysed yet — a normal state, not an error.
   * Every point is AI-authored prose, which is why the response carries `advisory: true` and why
   * the UI must label it rather than render it as part of the record.
   */
  getClinicalSummary(patientId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/patients/${patientId}/clinical-summary`, this.authHeaders());
  }

  /**
   * Which of these patients still have coding suggestions nobody has reviewed?
   *
   * One request for a whole billing export rather than one per patient — an export can cover a
   * hundred patients, and `getForPatient` in a loop would be a hundred round trips before the
   * user sees anything.
   *
   * The server applies the SAME visibility filter the review list uses, so a patient is only
   * reported when the review dialog would actually show them something. It also answers
   * `enabled: false` for a practice that has not opted in (§10), which the caller treats as
   * "nothing to review" rather than as an error.
   */
  getPendingSummary(groupId: string, patientIds: string[]): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/patients/icd-suggestions/pending-summary`,
      { groupId, patientIds },
      this.authHeaders(),
    );
  }

  /**
   * What the coding pipeline is doing for this patient right now.
   *
   * `data.state` is `idle` | `queued` | `running`, and `data.stage` names the batch stage while
   * running. Polled by the patient file while a run is in flight, so it is deliberately cheap
   * and deliberately incapable of failing loudly — the server answers `idle` rather than
   * erroring when it cannot tell, because a status probe that throws would leave the button
   * stuck in whatever state it last saw.
   */
  getRunStatus(patientId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/patients/${patientId}/icd-run-status`, this.authHeaders());
  }

  /**
   * Queues a whole-record coding review for this patient.
   *
   * ⚠ THIS SPENDS THE GROUP'S AI BALANCE. The caller must confirm with the user first — see
   * `runIcdPipeline` on the patient file, which is the only caller and does exactly that.
   *
   * Answers **202**, not 200: the work is accepted, not performed. Both stages run through the
   * OpenAI Batch API, so suggestions appear minutes to hours later, never on this response.
   *
   * **409** means a run is already queued or running for this patient. That is the authoritative
   * double-run guard — the disabled button only binds the tab it is in, while two clinicians on
   * two machines both reach this.
   */
  triggerRun(patientId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/patients/${patientId}/icd-run`, {}, this.authHeaders());
  }
}
